import { colors } from '@mathflat/common/style'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'

import _Badge from '@/component/atoms/Badge/_Badge'
import { _OldButton } from '@/component/atoms/Button/_OldButton'
import useRepository from '@/hooks/useRepository'
import modalService from '@/service/modal.service'

import { ContractModalService } from '../ContractModal.service'
import { S } from '../ContractModal.style'
import ContractPdf from './ContractPdf'
import SignatureModal from './SignatureModal'

const errorButtonStyle: React.CSSProperties = { borderColor: colors.red.$400 }

const ContractStep2 = () => {
  const service = useRepository(ContractModalService)
  const [goNextStepTrigged, setGoNextStepTrigged] = useState(false)

  const isViewedPdfContract = service.viewPdfContract.isDirty && service.viewPdfContract.isValid
  const invalidContractViewed =
    (!service.viewPdfContract.isValid || !service.viewPdfContract.isDirty) && goNextStepTrigged
  const invalidSchoolNameSignature =
    (!service.schoolNameSignature.isValid || !service.schoolNameSignature.isDirty) &&
    goNextStepTrigged
  const invalidTeacherSignature =
    (!service.teacherSignature.isValid || !service.teacherSignature.isDirty) && goNextStepTrigged

  useEffect(() => {
    // 다음 단계로 넘어가기를 시도한 후 viewPdfContract 값을 수정했을때 초기화 해주어야 하므로
    setGoNextStepTrigged(false)
  }, [service.viewPdfContract.isDirty, service.viewPdfContract.isValid])
  return (
    <>
      {service.uiStore.contractPdfView ? (
        <>
          <ContractPdf />
          <S.Footer className="contract-footer">
            <p className="error-message align-right">
              계약 내역서를 스크롤하여 하단까지
              <br /> 모두 확인할 경우 확인 버튼이 활성화됩니다.
            </p>
            <_OldButton
              disabled={!service.viewPdfContract.isDirty || !service.viewPdfContract.isValid}
              theme="primary"
              width={144}
              onClick={() => service.uiStore.toggleContractView()}
            >
              확인
            </_OldButton>
          </S.Footer>
        </>
      ) : (
        <>
          <section className="modal-body">
            <ul className="modal-step-wrapper">
              <li className="modal-step">step 1</li>
              <li className="modal-step active">step 2</li>
            </ul>
            <header className="modal-body-header">
              <h2>계약 내역서 확인 및 서명</h2>
              <strong>계약서를 꼭 확인해주세요! 계약서를 확인한 후 서명이 가능합니다.</strong>
            </header>
            <S.ModalContent className="open active">
              <header className="step-header">
                <_Badge
                  size="large"
                  background={colors.blue.$100}
                  color={colors.blue.$300}
                  border
                  style={{ fontWeight: 'bold', borderColor: colors.blue.$200 }}
                >
                  1단계
                </_Badge>
                <h3 className="step-header-title">계약서 확인하기</h3>
              </header>
              <section className="step-content">
                <article className="contract-pdf-content">
                  <h4>계약 내역서*</h4>
                  <_OldButton
                    theme={service.viewPdfContract.isValid ? 'individual-select' : 'individual'}
                    style={invalidContractViewed ? errorButtonStyle : undefined}
                    width={144}
                    height={40}
                    onClick={() => {
                      service.uiStore.toggleContractView()
                    }}
                  >
                    계약서 확인하기
                  </_OldButton>
                </article>
                {invalidContractViewed && (
                  <p className="error-message align-right">
                    {service.viewPdfContract.errorMessage}
                  </p>
                )}
              </section>
            </S.ModalContent>
            <S.ModalContent className={clsx('', isViewedPdfContract && 'open active')}>
              <header className="step-header">
                <_Badge
                  size="large"
                  background={isViewedPdfContract ? colors.blue.$100 : 'transparent'}
                  color={isViewedPdfContract ? colors.blue.$300 : colors.gray.$500}
                  border
                  style={{
                    fontWeight: 'bold',
                    borderColor: isViewedPdfContract ? colors.blue.$200 : colors.gray.$400,
                  }}
                >
                  2단계
                </_Badge>
                <h3 className="step-header-title">서명하기</h3>
              </header>
              <section className="step-content">
                <article className="signature-content">
                  <div>
                    <h4>학교 서명 입력*</h4>
                    <_OldButton
                      theme={
                        service.schoolNameSignature.isValid ? 'individual-select' : 'individual'
                      }
                      style={invalidSchoolNameSignature ? errorButtonStyle : undefined}
                      width={320}
                      height={40}
                      onClick={() =>
                        modalService.openModal(
                          <SignatureModal
                            placeHolder="학교"
                            onSign={(sign) => {
                              service.setSchoolNameSignature(sign)
                            }}
                            sign={service.schoolNameSignature.content}
                          />,
                        )
                      }
                    >
                      학교 서명하기
                    </_OldButton>
                  </div>
                  {invalidSchoolNameSignature && (
                    <p className="error-message align-right">
                      {service.schoolNameSignature.errorMessage}
                    </p>
                  )}
                </article>
                <article className="signature-content">
                  <div className="signature-content">
                    <h4>신청인 서명 입력*</h4>
                    <_OldButton
                      theme={service.teacherSignature.isValid ? 'individual-select' : 'individual'}
                      style={invalidTeacherSignature ? errorButtonStyle : undefined}
                      width={320}
                      height={40}
                      onClick={() =>
                        modalService.openModal(
                          <SignatureModal
                            placeHolder="서명자"
                            onSign={(sign) => {
                              service.setTeacherSignature(sign)
                            }}
                            sign={service.teacherSignature.content}
                          />,
                        )
                      }
                    >
                      신청인 서명하기
                    </_OldButton>
                  </div>
                  {invalidTeacherSignature && (
                    <p className="error-message align-right">
                      {service.teacherSignature.errorMessage}
                    </p>
                  )}
                </article>
              </section>
            </S.ModalContent>
            <S.ModalInfoContent>
              <header className="step-header">안내사항</header>
              <div className="step-content">
                <p>
                  관리자 계정에서 스쿨플랫 정식 가입 계약 내역서에 동의/서명하신 후에 제품 이용이
                  가능합니다.
                </p>
                <p>
                  동의/서명 완료하시면, 관리자 계정의 {"‘내 정보'"}에서 계약 내역서를 다운로드
                  받으실 수 있습니다.
                </p>
              </div>

              <b>고객센터 1670 - 1930</b>
            </S.ModalInfoContent>
          </section>
          <S.Footer>
            <_OldButton width={144} onClick={() => service.uiStore.goPrevStep()}>
              이전으로
            </_OldButton>
            <_OldButton
              theme="primary"
              width={144}
              style={{ fontWeight: 700 }}
              onClick={() => {
                setGoNextStepTrigged(true)
                if (
                  !service.viewPdfContract.isValid ||
                  !service.viewPdfContract.isDirty ||
                  !service.teacherSignature.isValid ||
                  !service.teacherSignature.isDirty ||
                  !service.schoolNameSignature.isValid ||
                  !service.schoolNameSignature.isDirty
                ) {
                  return
                }
                service.uiStore.goNextStep()
              }}
            >
              제출하기
            </_OldButton>
          </S.Footer>
        </>
      )}
    </>
  )
}

export default observer(ContractStep2)
